package at.theatervogelweide.admin.contract.accounts

import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.validate
import kotlinx.datetime.LocalDateTime
import kotlinx.serialization.Serializable

// Konten (#66): the sign-in accounts, managed by superusers only. E-mail addresses are personal data: nothing logs
// them. Timestamps are in UTC, as stored in the database.

/** What an account may do; stored as Spring's authorities, e.g. `ROLE_SUPERUSER,ROLE_ADMIN`. */
@Serializable
enum class AccountRole(val label: String, val description: String) {
    ADMIN("Admin", "Bearbeitet die Inhalte."),
    SUPERUSER("Superuser", "Bearbeitet die Inhalte und verwaltet die Konten."),
}

/** A row of the table. */
@Serializable
data class AccountListEntry(
    val id: Long,
    val version: Long,
    val email: String,
    /** The linked person's name. */
    val personName: String?,
    /** `null` for an account without the admin role, which can't sign in. */
    val role: AccountRole?,
    val enabled: Boolean,
    /** `false` until the account holder used the first one-time link. */
    val passwordSet: Boolean,
    /** The signed-in superuser's own account. */
    val self: Boolean,
)

/** What a superuser edits. */
@Serializable
data class AccountDraft(
    val email: String,
    val role: AccountRole?,
    /** News published "unter meinem Namen" show this person's name. */
    val personId: Long? = null,
    val enabled: Boolean = true,
) {
    fun validate(): List<FieldError> = validate {
        field(EMAIL, email) { required(); maxLength(255); email() }
        check(ROLE, role != null) { "Bitte eine Rolle wählen." }
    }

    companion object {
        const val EMAIL = "email"
        const val ROLE = "role"
        const val PERSON = "person"
        const val ENABLED = "enabled"
    }
}

/** A saved account, as the editor loads it. */
@Serializable
data class AccountEditor(
    val id: Long,
    val version: Long,
    val draft: AccountDraft,
    val personName: String?,
    val passwordSet: Boolean,
    /** Until when the open one-time link works; the link itself is only shown once, when it is created. */
    val linkExpiresAt: LocalDateTime?,
    /** The signed-in superuser's own account: it can't be disabled, demoted or deleted. */
    val self: Boolean,
    /** The news items published under this account's name; deleting it leaves them without author. */
    val newsCount: Int,
)

/** A one-time link to set the password; shown once, valid until [expiresAt]. */
@Serializable
data class PasswordLink(
    val url: String,
    val expiresAt: LocalDateTime,
)

@Serializable
data class AccountWithLink(
    val account: AccountEditor,
    val link: PasswordLink,
)
